import { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

export const AuthContext = createContext(null);

// M6 (backend implementation review): posting, applying, refunding, voiding or
// recouping money requires the billing or admin role. The SERVER enforces it
// (`requireMoneyRole`); this only keeps a staff user from pressing a button that
// can only answer 403. Keep the list in step with `moneyRole.middleware.js`.
export const MONEY_ROLES = Object.freeze(['billing', 'admin']);
export const MONEY_ROLE_HINT = 'Moving money requires the billing or admin role.';

export function canMoveMoney(user) {
  return Boolean(user && MONEY_ROLES.includes(user.role));
}

/**
 * Whether the signed-in user may move money. Reads the context directly rather
 * than through `useAuth`, so a component rendered outside a provider (an
 * isolated test) gets a clear FALSE — the safe direction — instead of a throw.
 */
export function useCanMoveMoney() {
  return canMoveMoney(useContext(AuthContext)?.user);
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mfaPending, setMfaPending] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.get('/api/auth/me')
        .then(data => setUser(data.user))
        .catch(() => localStorage.removeItem('token'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (identifier, password, rememberMe) => {
    const deviceToken = localStorage.getItem('deviceToken');

    try {
      const data = await api.post('/api/auth/login', { identifier, password, rememberMe, deviceToken });

      if (data.mfaRequired) {
        setMfaPending({ mfaToken: data.mfaToken, rememberMe });
        return { mfaRequired: true };
      }

      localStorage.setItem('token', data.token);
      setUser(data.user);
      return { success: true, user: data.user };
    } catch (error) {
      const responseData = error.response?.data;
      if (responseData?.needsVerification) {
        return { needsVerification: true, email: responseData.email };
      }
      throw error;
    }
  };

  const verifyMfa = async (code, rememberDevice) => {
    if (!mfaPending) throw new Error('No MFA session pending');

    const data = await api.post('/api/auth/mfa/verify', {
      mfaToken: mfaPending.mfaToken,
      code,
      rememberDevice,
      rememberMe: mfaPending.rememberMe
    });

    localStorage.setItem('token', data.token);
    if (data.deviceToken) {
      localStorage.setItem('deviceToken', data.deviceToken);
    }
    setUser(data.user);
    setMfaPending(null);
    return { success: true };
  };

  const updateUser = (updates) => {
    setUser((prev) => prev ? { ...prev, ...updates } : prev);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setMfaPending(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, mfaPending, login, logout, verifyMfa, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}
